import { LIMITE_DEVOLUCIONES, type CalcularDevolucionInput, type CalculoDevolucionDTO, type DevolucionInput, type DevolucionResultadoDTO, type DocumentoDTO, type NotaDebitoInput, type OrigenDevolucionDTO, type OtroNombreInput, type ReintegroDTO, type ReintegroPagarInput } from '@mg/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, get, post } from '@/lib/api.ts';

export interface DevolucionResumen {
  id: string;
  tipo: 'NC' | 'ND' | 'CAM';
  numero_formateado: string;
  control_formateado: string | null;
  documento_origen_numero: string | null;
  cliente_razon_social: string;
  cliente_rif: string;
  emitido_en: string;
  total: string;
  /** Bs del papel y tasas del documento (solo para mostrarlos). */
  total_bs: string;
  tasa_bcv: string;
  tasa_aplicada: string;
  credito_aplicado: string;
  saldo_pendiente: string;
  motivo: string | null;
  cajero_nombre: string;
  /** Una hoja anulada sigue en el listado, marcada como tal. */
  estado: 'emitido' | 'anulado';
  /** Devolución de dinero por transferencia: pendiente o pagada. */
  reintegro_estado: 'pendiente' | 'pagado' | null;
  /** Lo que el cliente se llevó (nota de despacho o factura nueva). */
  despacho_numero: string | null;
}

export type FiltrosDevoluciones = { q?: string | undefined; desde?: string | undefined; hasta?: string | undefined };
export const clavesDevoluciones = {
  lista: (f: FiltrosDevoluciones) => ['devoluciones', 'lista', f] as const,
  origen: (q: string) => ['devoluciones', 'origen', q] as const,
};

/** El listado pide el máximo que admite la API; si llega al tope, la pantalla avisa que hay más (acotar fechas o buscar). */
export const TOPE_LISTA_DEVOLUCIONES = LIMITE_DEVOLUCIONES;
export const useDevoluciones = (f: FiltrosDevoluciones) => useQuery({ queryKey: clavesDevoluciones.lista(f), queryFn: () => get<DevolucionResumen[]>('/devoluciones', { q: f.q || undefined, desde: f.desde || undefined, hasta: f.hasta || undefined, limite: TOPE_LISTA_DEVOLUCIONES }), placeholderData: (p) => p });
/** La búsqueda del documento origen trae a lo sumo estos (los más recientes); si llega al tope, la pantalla lo avisa (hallazgo 2-H16). */
export const TOPE_BUSQUEDA_ORIGEN = 10;
export const useOrigenes = (q: string) => useQuery({ queryKey: clavesDevoluciones.origen(q), queryFn: () => get<OrigenDevolucionDTO[]>('/devoluciones/origen', { q: q || undefined, limite: TOPE_BUSQUEDA_ORIGEN }), enabled: q.trim().length > 0 });
export const calcularDevolucion = (datos: CalcularDevolucionInput) => post<CalculoDevolucionDTO>('/devoluciones/calcular', datos);

function useInvalidar() {
  const qc = useQueryClient();
  return () => Promise.all([qc.invalidateQueries({ queryKey: ['devoluciones'] }), qc.invalidateQueries({ queryKey: ['ventas'] }), qc.invalidateQueries({ queryKey: ['productos'] }), qc.invalidateQueries({ queryKey: ['clientes'] }), qc.invalidateQueries({ queryKey: ['inventario'] }), qc.invalidateQueries({ queryKey: ['cierres'] })]);
}
export function useRegistrarDevolucion() {
  const inv = useInvalidar();
  return useMutation({ mutationFn: ({ datos, clave }: { datos: DevolucionInput; clave: string }) => api<DevolucionResultadoDTO>('POST', '/devoluciones', { body: datos, headers: { 'idempotency-key': clave } }), onSuccess: () => void inv() });
}
/** «Facturar a otro nombre» (spec 08, enmienda 2026-10-29): NC total + factura (o nota) nueva al cliente nuevo pagada con ese crédito. */
export function useOtroNombre() {
  const inv = useInvalidar();
  return useMutation({ mutationFn: ({ datos, clave }: { datos: OtroNombreInput; clave: string }) => api<DevolucionResultadoDTO>('POST', '/devoluciones/otro-nombre', { body: datos, headers: { 'idempotency-key': clave } }), onSuccess: () => void inv() });
}
export function useRegistrarNotaDebito() {
  const inv = useInvalidar();
  return useMutation({ mutationFn: ({ datos, clave }: { datos: NotaDebitoInput; clave: string }) => api<DocumentoDTO>('POST', '/devoluciones/nd', { body: datos, headers: { 'idempotency-key': clave } }), onSuccess: () => void inv() });
}

// Devoluciones de dinero autorizadas (se pagan por transferencia, fuera de la caja)
export const useReintegros = (habilitado: boolean) => useQuery({ queryKey: ['devoluciones', 'reintegros'], queryFn: () => get<ReintegroDTO[]>('/devoluciones/reintegros'), enabled: habilitado });
export function usePagarReintegro() {
  const inv = useInvalidar();
  return useMutation({ mutationFn: ({ id, datos }: { id: string; datos: ReintegroPagarInput }) => post<ReintegroDTO>(`/devoluciones/reintegros/${id}/pagar`, datos), onSuccess: () => void inv() });
}
